import { useState } from 'react';
import { 
  Users, 
  Eye, 
  CreditCard, 
  Clock,
  DollarSign,
  TrendingUp,
  Percent
} from 'lucide-react';
import {
  StatsCard,
  DashboardHeader,
  Sidebar,
  MobileNav,
  TrafficChart,
  SourcesChart,
  GeoChart,
  TopPagesTable,
  FunnelChart,
  RecentActivity,
  DeviceChart
} from '../components/dashboard';
import {
  websiteInfo,
  trafficData,
  summaryStats,
  trafficSources,
  geoData,
  topPages,
  registrationFunnel,
  deviceData,
  recentActivity
} from '../data/analyticsData';

export default function AnalyticsDashboard() {
  const [activeTab, setActiveTab] = useState('overview');
  const [timeRange, setTimeRange] = useState('30days');

  // Get traffic data based on time range
  const getTrafficData = () => {
    switch (timeRange) {
      case 'today':
        return trafficData.daily.slice(-1);
      case '7days':
        return trafficData.daily.slice(-7);
      case '90days':
        return trafficData.daily;
      default:
        return trafficData.daily;
    }
  };

  return (
    <div className="min-h-screen bg-background noise-texture" data-testid="analytics-dashboard">
      {/* Sidebar */}
      <Sidebar activeTab={activeTab} onTabChange={setActiveTab} />
      
      {/* Mobile Navigation */}
      <MobileNav activeTab={activeTab} onTabChange={setActiveTab} />
      
      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen pb-20 lg:pb-0">
        <div className="max-w-7xl mx-auto p-4 lg:p-8">
          {/* Header */}
          <DashboardHeader 
            websiteInfo={websiteInfo}
            realTimeUsers={summaryStats.realTimeUsers}
            timeRange={timeRange}
            onTimeRangeChange={setTimeRange}
          />
          
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Stats Grid */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
                <StatsCard
                  title="Total Visitors"
                  value={summaryStats.totalVisitors.toLocaleString()}
                  subtitle="Last 30 days"
                  trend={summaryStats.trends.visitors}
                  icon={Users}
                />
                <StatsCard
                  title="Page Views"
                  value={summaryStats.totalPageViews.toLocaleString()}
                  subtitle="Last 30 days"
                  trend={summaryStats.trends.pageViews}
                  icon={Eye}
                />
                <StatsCard
                  title="Registrations"
                  value={summaryStats.totalRegistrations}
                  subtitle={`$${summaryStats.registrationValue.toLocaleString()} total`}
                  trend={summaryStats.trends.registrations}
                  icon={CreditCard}
                />
                <StatsCard
                  title="Avg. Time"
                  value={summaryStats.avgTimeOnSite}
                  subtitle={`${summaryStats.bounceRate}% bounce rate`}
                  trend={summaryStats.trends.bounceRate}
                  icon={Clock}
                />
              </div>

              {/* Charts Row 1 */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <TrafficChart data={getTrafficData()} timeRange={timeRange} />
                <SourcesChart data={trafficSources} />
              </div>

              {/* Charts Row 2 */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <GeoChart data={geoData} />
                <TopPagesTable data={topPages} />
              </div>
            </div>
          )}

          {/* Visitors Tab */}
          {activeTab === 'visitors' && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
                <StatsCard
                  title="Unique Visitors"
                  value={summaryStats.uniqueVisitors.toLocaleString()}
                  icon={Users}
                />
                <StatsCard
                  title="New Visitors"
                  value="3,421"
                  subtitle="57.7% of total"
                  icon={TrendingUp}
                />
                <StatsCard
                  title="Returning"
                  value="2,503"
                  subtitle="42.3% of total"
                  icon={Users}
                />
                <StatsCard
                  title="Avg. Session"
                  value={summaryStats.avgTimeOnSite}
                  icon={Clock}
                />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <GeoChart data={geoData} />
                <DeviceChart data={deviceData} />
              </div>

              <TrafficChart data={getTrafficData()} timeRange={timeRange} />
            </div>
          )}

          {/* Traffic Sources Tab */}
          {activeTab === 'traffic' && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
                {trafficSources.slice(0, 4).map((source, index) => (
                  <StatsCard
                    key={index}
                    title={source.source}
                    value={source.visitors.toLocaleString()}
                    subtitle={`${source.percentage}% of traffic`}
                    icon={Eye}
                  />
                ))}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <SourcesChart data={trafficSources} />
                <RecentActivity data={recentActivity} />
              </div>
            </div>
          )}

          {/* Pages Tab */}
          {activeTab === 'pages' && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
                <StatsCard
                  title="Total Pages"
                  value="6"
                  subtitle="Sections tracked"
                  icon={Eye}
                />
                <StatsCard
                  title="Avg. Views/Page"
                  value="2,452"
                  icon={Eye}
                />
                <StatsCard
                  title="Top Page"
                  value="Home"
                  subtitle="5,842 views"
                  icon={TrendingUp}
                />
                <StatsCard
                  title="Avg. Bounce"
                  value={`${summaryStats.bounceRate}%`}
                  icon={Percent}
                />
              </div>

              <TopPagesTable data={topPages} />
            </div>
          )}

          {/* Registrations Tab */}
          {activeTab === 'conversions' && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
                <StatsCard
                  title="Total Registrations"
                  value={summaryStats.totalRegistrations}
                  trend={summaryStats.trends.registrations}
                  icon={CreditCard}
                />
                <StatsCard
                  title="Revenue Generated"
                  value={`$${summaryStats.registrationValue.toLocaleString()}`}
                  subtitle="at $1,500 per seat"
                  icon={DollarSign}
                />
                <StatsCard
                  title="Conversion Rate"
                  value={`${summaryStats.conversionRate}%`}
                  icon={Percent}
                />
                <StatsCard
                  title="Checkout Abandonment"
                  value="50.3%"
                  subtitle="312 started, 155 completed"
                  icon={TrendingUp}
                />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <FunnelChart data={registrationFunnel} />
                <RecentActivity data={recentActivity.filter(a => a.type === 'registration' || a.type === 'click')} />
              </div>
            </div>
          )}

          {/* Reports Tab */}
          {activeTab === 'reports' && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
                <StatsCard
                  title="Total Visitors"
                  value={summaryStats.totalVisitors.toLocaleString()}
                  trend={summaryStats.trends.visitors}
                  icon={Users}
                />
                <StatsCard
                  title="Page Views"
                  value={summaryStats.totalPageViews.toLocaleString()}
                  trend={summaryStats.trends.pageViews}
                  icon={Eye}
                />
                <StatsCard
                  title="Registrations"
                  value={summaryStats.totalRegistrations}
                  trend={summaryStats.trends.registrations}
                  icon={CreditCard}
                />
                <StatsCard
                  title="Revenue"
                  value={`$${summaryStats.registrationValue.toLocaleString()}`}
                  icon={DollarSign}
                />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <TrafficChart data={getTrafficData()} timeRange={timeRange} />
                <FunnelChart data={registrationFunnel} />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <DeviceChart data={deviceData} />
                <TopPagesTable data={topPages} />
              </div>
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div className="space-y-6">
              <div className="bg-card border border-white/5 rounded-xl p-6">
                <h2 className="text-xl font-playfair font-medium text-foreground mb-4">
                  Dashboard Settings
                </h2>
                <p className="text-muted-foreground font-manrope">
                  Analytics settings and configuration options would go here.
                </p>
                <div className="mt-6 p-4 bg-secondary/50 rounded-lg">
                  <p className="text-sm text-muted-foreground font-manrope">
                    <strong>Note:</strong> This analytics dashboard displays mock/demo data for demonstration purposes.
                    To connect real analytics data, integrate with Google Analytics API or a similar service.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
